/**
 * ListItemTile
 *
 * Shared clickable list row — icon (optional) + title + description (optional)
 * + an optional trailing slot (forward chevron by default, or caller-supplied
 * content such as a selection check mark). Used by settings rows, sheet menus,
 * and the home top-bar expanded menu. Consolidates the previously
 * feature-coupled `SettingsCategoryItem` and the parallel `SheetMenuRow`
 * primitives into a single common component.
 *
 * Visual contract is uniform across all consumers (matches the prior
 * `SettingsCategoryItem` styling): medium icon, lg title, sm description,
 * `responsive.gap.lg` between icon and text, `responsive.spacing.base`
 * padding. Do not branch styling on `description` / context.
 *
 * Follow-up: the language selector leg of that migration is being done on this
 * branch — its rows are `ListItemTile`s that pass a check mark through
 * `trailing`. A later task will migrate the remaining sheets (gift, theme
 * selector, etc.) to use this primitive so the entire app shares one row look.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ForwardChevronIcon } from '@/presentation/components/common/ForwardChevronIcon';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

export interface ListItemTileProps {
  /** Primary label rendered as the title line. */
  title: string;
  /** Optional asset path for the leading icon. When omitted the icon slot is not rendered. */
  iconPath?: string;
  /** Optional secondary text rendered below the title. When omitted the row is single-line. */
  description?: string;
  /** Icon tint. Defaults to `theme.icon.secondary` to match the legacy `SettingsCategoryItem` look. */
  iconColor?: string;
  /** Click handler. */
  onClick: () => void;
  /** When true, renders the trailing forward chevron. Defaults to false. */
  showChevron?: boolean;
  /**
   * Optional trailing content rendered at the end of the row (e.g. a selection
   * check mark). Takes precedence over `showChevron` when both are supplied.
   * The node owns its own sizing / colour — the row adds no wrapper or spacing.
   */
  trailing?: React.ReactNode;
  /** Optional `data-qa-*` set for the tappable row `<button>` so the qa-tester can locate it. */
  qa?: QaAttrOptions;
}

export const ListItemTile: React.FC<ListItemTileProps> = ({
  title,
  iconPath,
  description,
  iconColor,
  onClick,
  showChevron = false,
  trailing,
  qa,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <button
      {...(qa ? qaAttr(qa) : {})}
      onClick={onClick}
      className="w-full text-left transition-opacity hover:opacity-80"
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: responsive.spacing.base,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: responsive.gap.lg,
        }}
      >
        {iconPath && (
          <AssetIcon
            path={iconPath}
            width={responsive.icon.medium}
            height={responsive.icon.medium}
            color={iconColor ?? theme.icon.secondary}
          />
        )}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: responsive.font.lg,
              fontWeight: responsive.fontWeight.medium,
              color: theme.text.primary,
            }}
          >
            {title}
          </div>
          {description && (
            <div
              style={{
                fontSize: responsive.font.sm,
                color: theme.text.secondary,
                marginTop: responsive.spacing.xxxs,
              }}
            >
              {description}
            </div>
          )}
        </div>
        {trailing ?? (showChevron ? <ForwardChevronIcon /> : null)}
      </div>
    </button>
  );
};
